import * as React from "react";
import {cva, type VariantProps} from "class-variance-authority";

import {cn} from "@/lib/utils";

const buttonVariants = cva(
  "cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-palette-inverse text-white",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "bg-white hover:bg-muted hover:border-dark hover:text-label text-slate-600 border border-border shadow-sm",
        "outline-primary":
          "bg-white hover:bg-primary-background hover:border-primary hover:text-primary-accent text-slate-600 border border-border shadow-sm",
        "outline-destructive":
          "bg-transparent hover:bg-destructive-background hover:bg-red-50 text-red hover:text-red border border-red",
        "outline-inverse":
          "bg-transparent text-inverse border border-[#E4E4E730] hover:border-[#E4E4E760]",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground text-secondary",
        link: "text-primary-accent underline-offset-4 hover:underline",
        "destructive-outline":
          "bg-white hover:bg-destructive/8 text-destructive border border-destructive shadow-sm",
        primary:
          "bg-primary-background border-primary text-primary-accent border shadow-sm",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10 shrink-0",
        "icon-button": "h-9 w-9 shrink-0",
        "icon-sm": "h-8 w-8 shrink-0",
        "icon-xs": "h-6 w-6 shrink-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asDiv?: boolean;
}

const ButtonComponent = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({className, variant, size, asDiv, ...props}, ref) => {
    return (
      <button
        className={cn(buttonVariants({variant, size, className}))}
        ref={ref}
        {...props}
      />
    );
  },
);
ButtonComponent.displayName = "ButtonComponent";

ButtonComponent.displayName = "ButtonComponent";

const DivComponent = React.forwardRef<
  HTMLDivElement,
  Omit<ButtonProps, keyof React.ButtonHTMLAttributes<HTMLButtonElement>> & {
    className?: string;
  }
>(({className, variant, size, asDiv, ...props}, ref) => {
  return (
    <div
      className={cn(buttonVariants({variant, size, className}))}
      ref={ref}
      {...props}
    />
  );
});
DivComponent.displayName = "DivComponent";

DivComponent.displayName = "DivComponent";

const Button = React.forwardRef<
  HTMLButtonElement | HTMLDivElement,
  ButtonProps
>((props, ref) => {
  return props.asDiv ? (
    <DivComponent
      {...props}
      ref={ref as React.Ref<HTMLDivElement>}
      className={cn(
        props.className,
        props.disabled && "opacity-50 pointer-events-none",
      )}
    />
  ) : (
    <ButtonComponent
      {...props}
      ref={ref as React.Ref<HTMLButtonElement>}
      disabled={props.disabled}
    />
  );
});

Button.displayName = "Button";

export {Button, buttonVariants};
